import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { AnyAction } from 'redux';
import { connect } from 'react-redux';

import { fetchLocales, fetchTimeZones, logout } from './store/session/actions';
import { CompanyI, defaultFeatures, Features } from './interfaces/company.interface';
import Header from './shared/header/Header';
import { mediaSizes, MediaTypes } from './styles/media-templates';
import { GlobalStyles } from './styles/global.sc';
import PagesRoutes from './PagesRoutes';
import { StoreI } from './rootReducer';
import Footer from './shared/footer/Footer';
import SidebarError from './shared/sidebar/containers/SidebarError';
import { isFullPageRoute, routes } from './routingConfig';
import { ApiReduxResp } from './services/apiMiddleware';
import SnackbarNotifications from './shared/snackbar/SnackbarNotifications';
import { getAppConfig, setMediaSize } from './store/app/actions';
import { isAdmin } from './store/session/session.reducer';
import { GuidedTourPayload, initializeUserGuiding, selectGuidedTourPayload } from './shared/guidedTour';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './config/queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

interface StateProps {
  isAuthenticated: boolean;
  media?: MediaTypes;
  accountSuspended: boolean;
  isAdmin: boolean;
  simpleGui: boolean;
  features: Partial<Features>;
  company: CompanyI | null;
  guidedTourPayload?: GuidedTourPayload;
}

interface DispatchProps {
  logout(): AnyAction;
  changeMediaSize(mediaType: MediaTypes): AnyAction;
  fetchTimeZones(): ApiReduxResp;
  fetchLocales(): ApiReduxResp;
  getAppConfig(): void;
}

type Props = StateProps & DispatchProps & RouteComponentProps<any>;

class App extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.props.getAppConfig();
    this.checkViewPortWidth();
  }

  componentDidMount() {
    window.addEventListener('resize', this.checkViewPortWidth);
    this.props.fetchTimeZones();
    this.props.fetchLocales();
    if (this.props.accountSuspended) {
      this.props.history.push(routes.accountSuspended);
    }

    if (this.props.guidedTourPayload) {
      initializeUserGuiding(this.props.guidedTourPayload);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { accountSuspended, history } = this.props;
    if (accountSuspended && prevProps.accountSuspended !== accountSuspended) {
      history.push(routes.accountSuspended);
    }

    // TODO: didn't found any case for this, looks like redundant
    // if (!isAuthenticated && prevProps.isAuthenticated) {
    //   if (!accountSuspended) history.push(routes.login);
    //   this.props.logout();
    // }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.checkViewPortWidth);
  }

  checkViewPortWidth = () => {
    // iOS check
    let width = window.orientation === 0 ? window.screen.width : window.screen.height;
    // Android
    if (navigator.userAgent.indexOf('Android') >= 0) {
      width = window.screen.width;
    }
    const { media, changeMediaSize } = this.props;

    if (width <= mediaSizes.phone) {
      if (media !== 'phone') {
        changeMediaSize('phone');
      }
    } else if (width <= mediaSizes.tablet) {
      if (media !== 'tablet') {
        changeMediaSize('tablet');
      }
    } else if (media !== 'desktop') {
      changeMediaSize('desktop');
    }
  };

  render() {
    return (
      <QueryClientProvider client={queryClient}>
        
        {!isFullPageRoute() && this.props.isAuthenticated ? (
          <SidebarError />
        ) : (
          <Header
            history={this.props.history}
            locationPath={this.props.location.pathname}
            isAuthenticated={this.props.isAuthenticated}
            logout={this.props.logout}
          />
        )}
        {/* content shall not be hidden under left sidebar */}
        <PagesRoutes
          sidebarHidden={!this.props.isAdmin && this.props.simpleGui}
          mobile={this.props.media === 'phone'}
          isAuthenticated={this.props.isAuthenticated}
          location={this.props.location}
          history={this.props.history}
          features={this.props.features}
        />
        {!this.props.isAuthenticated && <Footer pathname={this.props.location.pathname} />}
        <SnackbarNotifications />
        <GlobalStyles />

        <ReactQueryDevtools initialIsOpen />
      </QueryClientProvider>
    );
  }
}

const mapState2Props = (state: StoreI): StateProps => {
  return {
    guidedTourPayload: selectGuidedTourPayload(state),
    isAuthenticated: state.user.session.authStatus,
    media: state.app.mediaQuery,
    accountSuspended: state.user.session.accountSuspended,
    isAdmin: isAdmin(state),
    simpleGui: state.user.session.company ? state.user.session.company.simple_gui : false,
    features: state.user.session.company ? state.user.session.company.features : defaultFeatures,
    company: state.user.session.company,
  };
};

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  changeMediaSize: (media: MediaTypes) => dispatch(setMediaSize(media)),
  logout: () => dispatch(logout),
  fetchTimeZones: () => dispatch(fetchTimeZones()),
  fetchLocales: () => dispatch(fetchLocales()),
  getAppConfig: () => dispatch(getAppConfig),
});

const AppWithConnect = connect(mapState2Props, mapDispatch2Props)(App);
const AppSuperComponent = withRouter(AppWithConnect);

export default AppSuperComponent;
